import Vue from 'vue';
import Vuex, { StoreOptions } from 'vuex';
import { RootState } from "./types"
import { Workspace } from '../models/Workspace';
import { Template } from '../models/Template';
import { WorkspaceDetails } from '../models/WorkspaceDetails'
import { Helper, WorkspaceDetailsParams } from '../models/HelperClasses'
import { ajaxGet, errorToaster } from "./ajax"
import { GraphUserModel } from '@/models/GraphUserModel';
import localforage from 'localforage';
import { GetWorkspacesParams, AddWorkspacesParams } from '@/models/StoreClasses'
import { SizeAndPrice, VmImage } from '@/models/VirtualMachine';
import { AuthModule } from '@/auth/AuthModule';
import { VreConfiguration } from '@/models/VreConfiguration';
Vue.use(Vuex);

var base = process.env.VUE_APP_ROOT_API;
var storeName = "VRE"
if (window.location.host.includes("preview")) {
  base = base.replace(base.split('.')[0], base.split('.')[0] + "-preview");
}

function initLocalForage() {
  localforage.config({
    driver: localforage.INDEXEDDB,
    name: storeName,
    version: 1.0,
    storeName: storeName
  });
}

async function getToken(authModule: AuthModule, forceRefresh: boolean = false){
  //wait for the login in initialize store
  while(!authModule.isLoggedIn){
    await Helper.timeout(100);
  }
  var token = await authModule.getapiTokenPopup(forceRefresh);
  return token;
}

function saveLocal(state: RootState) {
  localforage.setItem(storeName, state);
}

function log(...args: any[])  {
  Helper.log("ST", ...args);
}

function refreshCache(state: RootState, key: string, cacheInMinutes: number = 60): boolean {
  if (state.lastUpdated === undefined || state.lastUpdated[key] === undefined) {
    return true;
  }

  var expirationTime = new Date(state.lastUpdated[key].getTime() + cacheInMinutes * 60000);
  return new Date() > expirationTime;
}

const store: StoreOptions<RootState> = {
  state: {
    workspaces: {},
    templates: [],
    workspaceDetails: new WorkspaceDetails(),
    sizeAndPrices: [],
    vmImages: [],
    vreConfiguration: new VreConfiguration(),
    toaster: "",
    authModule: new AuthModule(),
    lastUpdated: {},
    isInitialized: false
  },
  mutations: {
    initializeStore(state:RootState, data) {
      Object.assign(state, data);
      if(!state.authModule.isLoggedIn){
        var authModule = new AuthModule();
        authModule.login("loginRedirect");
        Vue.set(state, 'authModule', authModule);
      }
    },
    setIsInitialized(state:RootState){
      state.isInitialized = true;
    },
    newUpdate(state:RootState, key) {
      state.lastUpdated[key] = new Date();
    },
    clearCacheForKey(state:RootState, key) {
      state.lastUpdated[key] = new Date("1970-01-01");
    },
    addWorkspaces(state: RootState, addWorkspacesParams: AddWorkspacesParams) {
      state.workspaces = {
        ...state.workspaces,
        [addWorkspacesParams.key]: addWorkspacesParams.workspaces
      };
    },
    addTemplates(state: any, templates: Template[]) {
      Vue.set(state, 'templates', templates);
    },
    addSizeAndPrices(state: any, sizeAndPrices: SizeAndPrice[]) {
      Vue.set(state, 'sizeAndPrices', sizeAndPrices);
    },
    addVmImages(state: any, vmImages: VmImage[]) {
      Vue.set(state, 'vmImages', vmImages);
    },
    addWorkspaceDetails(state: any, workspaceDetails: WorkspaceDetails) {
      state.workspaceDetails = {
        ...workspaceDetails
      };
    },
    addResearcherToWorkspace(state: RootState, user: GraphUserModel) {
      var workspaceDetails = state.workspaceDetails;
      workspaceDetails.researchers.push(user);
      state.workspaceDetails = Object.assign({}, workspaceDetails);
    },
    removeResearcherFromWorkspace(state: RootState, userToBeRemoved: GraphUserModel) {
      var workspaceDetails = state.workspaceDetails;
      workspaceDetails.researchers = workspaceDetails.researchers.filter(function (researcher) {
        return !(researcher.id === userToBeRemoved.id);
      })
      state.workspaceDetails = workspaceDetails;
    },
    setToaster(state: RootState, message: string) {
      Vue.set(state, "toaster", message);
    },
    setConfiguration(state: RootState, configuration: VreConfiguration){
      Vue.set(state, "vreConfiguration", configuration);
    },
    setWorkspaceVMStatus(state: RootState, wsStatus) {
      var workspaces = state.workspaces;
      let key = `workspaces|${state.authModule.userName}`;
      workspaces[key].find((ws:Workspace) => ws.resourceGroupName == wsStatus.workspaceName).setState(wsStatus.workspaceVMStatus);
      state.workspaces = workspaces;
    }
  },
  actions: {
    async initializeStore(context: any) {
      context.commit('initializeStore');
    },
    async getVreConfiguration(context: any) {
      let updateKey = "vreConfiguration";
      if (refreshCache(context.state, updateKey, 30)) {
        const url = base + "Configuration";
        var token = await getToken(context.state.authModule);
        var result = await ajaxGet(url, token);
        var vreConfiguration = new VreConfiguration(result);
        context.commit('setConfiguration', vreConfiguration);
        context.commit('newUpdate', updateKey);
      }
    },

    async preloadWorkspaces(context: any, getWorkspacesParams: GetWorkspacesParams) {
      var token = await getToken(context.state.authModule, getWorkspacesParams.forceRefresh);
      let updateKey = `preloadworkspaces|${getWorkspacesParams.userName}`;
      const preloadUrl = base + "Workspace/preload";
      var preloadResult = await ajaxGet(preloadUrl, token);
      const preLoadedWorkspaces: Workspace[] = [];
      preloadResult.forEach((ws: any) => {
      const preLoadedWorkspace = new Workspace(ws);
      preLoadedWorkspaces.push(preLoadedWorkspace);
      });
      let preloadedWorkspacesParams = new AddWorkspacesParams(updateKey, preLoadedWorkspaces);
      context.commit('addWorkspaces', preloadedWorkspacesParams);
      context.commit('newUpdate', updateKey);
    },

    async getWorkspaces(context: any, getWorkspacesParams: GetWorkspacesParams) {
      let updateKey = `workspaces|${getWorkspacesParams.userName}`;
      if (getWorkspacesParams.forceRefresh || refreshCache(context.state, updateKey)) {
        var token = await getToken(context.state.authModule, getWorkspacesParams.forceRefresh);
        const url = base + "Workspace";
        var result = await ajaxGet(url, token);
        const workspaces: Workspace[] = [];
        if (result !== null) {
            result.forEach((ws: any) => {
            const workspace = new Workspace(ws);
            workspaces.push(workspace);
          });
        }
        let addWorkspacesParams = new AddWorkspacesParams(updateKey, workspaces);
        context.commit('addWorkspaces', addWorkspacesParams);
        context.commit('newUpdate', updateKey);
      }
    },
    async getTemplates(context: any) {
      let updateKey = "templates";
      if (refreshCache(context.state, updateKey, 1440)) {
        const url = base + "Template/get";
        var token = await getToken(context.state.authModule);
        var result = await ajaxGet(url, token);
        const templates: Template[] = [];
        result.forEach((t: any) => {
          const template = new Template(t);
          templates.push(template);
        });
        context.commit('addTemplates', templates);
        context.commit('newUpdate', updateKey);
      }
    },
    async getSizeAndPrices(context: any) {
      let updateKey = "sizeAndPrices";
      if (refreshCache(context.state, updateKey, 1440)) {
        const url = base + "Workspace/GetSizeAndPrices";
        var token = await getToken(context.state.authModule);
        var result = await ajaxGet(url, token);
        const sizeAndPrices: SizeAndPrice[] = [];
        result.forEach((sp: any) => {
          const sizeAndPrice = new SizeAndPrice(sp);
          sizeAndPrices.push(sizeAndPrice);
        });
        context.commit('addSizeAndPrices', sizeAndPrices);
        context.commit('newUpdate', updateKey);
      }
    },
    async getVmImages(context: any) {
      let updateKey = "vmImages";
      if (refreshCache(context.state, updateKey, 1440)) {
        const url = base + "Workspace/GetVmImages";
        var token = await getToken(context.state.authModule);
        var result = await ajaxGet(url, token);
        const vmImages: VmImage[] = [];
        result.forEach((image: any) => {
          const sizeAndPrice = new VmImage(image);
          vmImages.push(sizeAndPrice);
        });
        context.commit('addVmImages', vmImages);
        context.commit('newUpdate', updateKey);
      }
    },
    async getWorkspaceDetails(context: any, params: WorkspaceDetailsParams) {
      const url = base + `Workspace/${params.resourceGroupName}/subscription/${params.subscriptionId}/details`;
      var token = await getToken(context.state.authModule);
      var oldVmMap = WorkspaceDetails.getVmMap(context.state.workspaceDetails);
      log("retrieving workspace details...");

      var result = await ajaxGet(url, token);
      var workspaceDetails = new WorkspaceDetails(result);
      if (workspaceDetails === undefined || workspaceDetails.virtualMachines === undefined) {
        if (result.error == null)
          errorToaster("Failed to retrieve the workspacedetails");
        else {
          errorToaster(result.error);
        }
        return;
      }

      var promiseArray: Array<Promise<void>> = [];
      workspaceDetails.virtualMachines.forEach(vm => {
        vm.setBackgroundColor();
        var vmUrl = base + `Workspace/${params.resourceGroupName}/subscription/${params.subscriptionId}/virtualMachine/${vm.name}/sizetypes/${vm.powerState === 'VM deallocated'}`;
        promiseArray.push(
        ajaxGet(vmUrl, token).then(function (result) {
            if (!result)
              return;
            result.forEach((element: SizeAndPrice) => {
              element.pricePerHour = element.pricePerMonthInEuro / 729.77;
          });
          vm.sizes = result;
        }));
      });
      if (params.storageName) {
        var storageUrl = base + `Workspace/${params.resourceGroupName}/subscription/${params.subscriptionId}/storage/${params.storageName}/items`;
        promiseArray.push(
        ajaxGet(storageUrl, token).then(function (storageData) {
          workspaceDetails.workspaceItems = storageData;
          }));
      }
 
      await Promise.all(promiseArray);
      log("...workspace details retrieved")
      WorkspaceDetails.fixVmStates(workspaceDetails, oldVmMap, context.state.vreConfiguration.vmUpdateStateHoldInterval);
      context.commit('addWorkspaceDetails', workspaceDetails);
    },
    async getWorkspaceVMStatus(context: any, params: WorkspaceDetailsParams) {
      if (params.subscriptionId == null || params.resourceGroupName == null)
        return;
      const url = base + `Workspace/${params.resourceGroupName}/subscription/${params.subscriptionId}/primarystatus`;
      var token = await getToken(context.state.authModule);
      var vmStatus = await ajaxGet(url, token);
      context.commit('setWorkspaceVMStatus', {
        workspaceName: params.resourceGroupName,
        workspaceVMStatus: vmStatus
      });
    }
  },
};

export default new Vuex.Store<RootState>(store);
